import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import {
  LuCalendarDays,
  LuCircleCheck,
  LuClipboardList,
  LuClock,
  LuDownload,
  LuEye,
  LuHospital,
  LuMapPin,
  LuUser,
  LuX,
} from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { Field, TextInput } from '../../components/form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';
import { EmptyState, HelpNote, LoadingRows } from './components/ProfileUi';
import VisitDetailsDialog from './projectVisits/VisitDetailsDialog';
// Eski sayfaların kullandığı :root renk değişkenleri burada tanımlı
import './ProjectVisitsModal.module.css';

/**
 * Bir çalışanın bir projedeki visitleri: tarih aralığı filtresi, Excel dökümü
 * ve tamamlanan visitlerin ayrıntıları. Profil › Projelerim ve rapor
 * sayfaları (Bölge Müdürleri, Çalışan Analizi/Raporları, SMO Lead) kullanır.
 */
const ProjectVisitsModal = ({ show, onHide, project, employeeId, employeeName }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.projectVisits.${key}`, vars);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [hospitals, setHospitals] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredVisits, setFilteredVisits] = useState([]);

  useEffect(() => {
    const fetchHospitalsAndVisits = async () => {
      try {
        // Önce hastaneleri çek
        const centersCollection = collection(db, 'Centers');
        const centersSnapshot = await getDocs(centersCollection);
        const hospitalsMap = {};
        centersSnapshot.docs.forEach(doc => {
          hospitalsMap[doc.id] = doc.data().Adı;
        });
        setHospitals(hospitalsMap);
        
        // Visit koleksiyonunu projeye ve çalışana göre filtrele
        const visitsRef = collection(db, 'Visits');
        const projectVisitsQuery = query(
          visitsRef,
          where('projectId', '==', project.id),
          where('employeeId', '==', employeeId)
        );
        const visitsSnapshot = await getDocs(projectVisitsQuery);

        const visitsData = visitsSnapshot.docs
          .map(doc => {
            const visitData = doc.data();
            return {
              id: doc.id,
              projectId: visitData.projectId,
              ...visitData,
              hospitalName: hospitalsMap[visitData.hospital] || visitData.hospital || '',
              patientId: visitData.patientId || '',
              city: visitData.city || '',
              status: visitData.status || false,
              visitType: visitData.visitType || '',
              // Takvimden açılan vizitlerde `date`, form kaydedilene kadar boştur;
              // takvimdeki gibi planlanan gün (firstVisitDate) gösterilir.
              date: visitData.date || visitData.firstVisitDate || ''
            };
          })
          .filter(visit => String(visit.date).trim() !== '');

        setVisits(visitsData);

      } catch (error) {
        console.error('Veri çekme hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    if (show && project?.id && employeeId) {
      fetchHospitalsAndVisits();
    }
  }, [show, project?.id, employeeId]);

  useEffect(() => {
    if (visits.length > 0) {
      const filtered = visits.filter(visit => {
        if (!startDate && !endDate) return true;

        const visitDate = new Date(visit.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          return visitDate >= start && visitDate <= end;
        } else if (start) {
          return visitDate >= start;
        } else if (end) {
          return visitDate <= end;
        }
        return true;
      });
      setFilteredVisits(filtered);
    }
  }, [visits, startDate, endDate]);

  const getVisitTypeLabel = (type) => {
    const types = {
      hastane: 'Hastane',
      home: 'Home',
      telefon: 'Telefon',
      unplanning: 'Unplanning',
      handover: 'Handover'
    };
    return types[type] || type;
  };

  const handleShowDetails = (visit) => {
    setSelectedVisit(visit);
  };

  const handleDownloadVisits = () => {
    const rows = filteredVisits.map(visit => ({
      'Tarih': visit.date || '-',
      'Visit Tipi': getVisitTypeLabel(visit.visitType) || '-',
      'Hasta ID': visit.patientId || '-',
      'Hastane': visit.hospitalName || '-',
      'Şehir': visit.city || '-',
      'Durum': visit.status ? 'Tamamlandı' : 'Devam Ediyor'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Proje Visitleri');

    const projectLabel = project?.titleShort || project?.title || 'Proje';
    const fileName = `Proje_Visitleri_${projectLabel}${employeeName ? `_${employeeName}` : ''}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const typeText = (type) => {
    if (!type) return '-';
    const translated = t(`profilePage.calendar.visitTypes.${type}`);
    return translated.startsWith('profilePage.') ? getVisitTypeLabel(type) : translated;
  };
  const formatDay = (value) => {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const projectLabel = project?.titleShort || project?.title || 'N/A';
  const completedCount = filteredVisits.filter((visit) => visit.status).length;
  const hasDates = Boolean(startDate || endDate);

  const statusBadge = (visit) => (
    <Badge tone={visit.status ? 'success' : 'warning'}>
      {visit.status ? <LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" /> : <LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />}
      {visit.status ? label('status.done') : label('status.ongoing')}
    </Badge>
  );
  const detailButton = (visit, className = '') => (
    <Button
      size="sm"
      onClick={() => handleShowDetails(visit)}
      disabled={!visit.status}
      title={!visit.status ? label('detailDisabled') : undefined}
      className={className}
    >
      <LuEye aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      {label('detail')}
    </Button>
  );

  let body;
  if (loading) {
    body = <LoadingRows count={5} height="tw-h-12" label={label('loading')} />;
  } else if (visits.length === 0) {
    body = <EmptyState icon={LuClipboardList} title={label('empty')} text={label('emptyHint')} />;
  } else {
    body = (
      <div className="tw-space-y-4">
        <HelpNote>{label('help')}</HelpNote>

        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <Field label={label('startDate')} htmlFor="project-visits-start" className="tw-w-full sm:tw-w-48">
            <TextInput id="project-visits-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={label('endDate')} htmlFor="project-visits-end" className="tw-w-full sm:tw-w-48">
            <TextInput id="project-visits-end" type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
          {hasDates && (
            <Button
              variant="ghost"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
            >
              <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('clearDates')}
            </Button>
          )}
          <div className="tw-flex tw-flex-wrap tw-gap-1.5 sm:tw-ml-auto sm:tw-pb-2">
            <Badge tone="success">{label('completed', { count: completedCount })}</Badge>
            <Badge tone="warning">{label('ongoing', { count: filteredVisits.length - completedCount })}</Badge>
          </div>
        </div>

        {filteredVisits.length === 0 ? (
          <EmptyState compact icon={LuCalendarDays} title={label('noMatch')} text={label('noMatchHint')} />
        ) : (
          <>
            {/* Tablet ve masaüstü: tablo */}
            <div className="tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 md:tw-block dark:tw-border-white/10">
              <table className="tw-w-full tw-border-collapse tw-text-left tw-text-sm">
                <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
                  <tr>
                    {['date', 'type', 'patient', 'hospital', 'city', 'status', 'action'].map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className={`tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400 ${key === 'action' ? 'tw-text-right' : ''}`}
                      >
                        {label(`columns.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]">
                  {filteredVisits.map((visit) => (
                    <tr key={visit.id} className="tw-transition-colors hover:tw-bg-slate-900/[0.02] dark:hover:tw-bg-white/[0.03]">
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-font-medium tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{visit.date ? formatDay(visit.date) : '-'}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-slate-700 dark:tw-text-slate-200">{typeText(visit.visitType)}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-font-mono tw-text-xs tw-text-slate-700 dark:tw-text-slate-200">{visit.patientId || '-'}</td>
                      <td className="tw-max-w-[16rem] tw-truncate tw-px-4 tw-py-2.5 tw-text-slate-700 dark:tw-text-slate-200" title={visit.hospitalName || undefined}>{visit.hospitalName || '-'}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-slate-700 dark:tw-text-slate-200">{visit.city || '-'}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5">{statusBadge(visit)}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2 tw-text-right">{detailButton(visit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Telefon: kart listesi */}
            <ul className="tw-space-y-2 md:tw-hidden">
              {filteredVisits.map((visit) => (
                <li key={visit.id} className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <div className="tw-min-w-0">
                      <p className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{visit.date ? formatDay(visit.date) : '-'}</p>
                      <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{typeText(visit.visitType)}</p>
                    </div>
                    {statusBadge(visit)}
                  </div>
                  <p className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
                    <span className="tw-inline-flex tw-items-center tw-gap-1"><LuUser aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />{visit.patientId || '-'}</span>
                    <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1"><LuHospital aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" /><span className="tw-truncate">{visit.hospitalName || '-'}</span></span>
                    <span className="tw-inline-flex tw-items-center tw-gap-1"><LuMapPin aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />{visit.city || '-'}</span>
                  </p>
                  {detailButton(visit, 'tw-w-full')}
                  {!visit.status && <p className="tw-text-center tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{label('detailDisabled')}</p>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={Boolean(show)}
        onClose={onHide}
        size="xl"
        title={label('title')}
        subtitle={`${projectLabel}${employeeName ? ` · ${employeeName}` : ''}`}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-sky-400 tw-to-indigo-600 tw-shadow-sky-500/30" size="xs">
            <LuClipboardList aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <span className="tw-mr-auto tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-600 max-sm:tw-hidden dark:tw-text-slate-300">
              {label('total', { count: filteredVisits.length })}
            </span>
            <Button variant="ghost" onClick={onHide}>{label('close')}</Button>
            <Button variant="success" onClick={handleDownloadVisits} disabled={filteredVisits.length === 0} className="max-sm:tw-flex-1">
              <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('excel')}
            </Button>
          </>
        )}
      >
        {body}
      </Modal>

      {selectedVisit && (
        <VisitDetailsDialog
          show={!!selectedVisit}
          onHide={() => setSelectedVisit(null)}
          visit={{
            ...selectedVisit,
            hospital: hospitals[selectedVisit.hospital] || selectedVisit.hospital
          }}
        />
      )}
    </>
  );
};

ProjectVisitsModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  project: PropTypes.object,
  employeeId: PropTypes.string,
  employeeName: PropTypes.string,
};

export default ProjectVisitsModal;
